/**
 * 渠道账号未读消息数 - Pinia store
 *
 * 轮询 GET /api/chat-records/accounts/unread，供三处使用：
 * 1. 访客会话左侧账号列表的未读徽标
 * 2. 会话列表「实时对话」tab 的角标
 * 3. 新消息声音提醒
 *
 * 声音提醒以「未读数比上一轮变大」为准，而不是靠 IM 推送：
 * IM 只推坐席已绑定账号下的消息，覆盖不到全部账号；轮询接口按可见账号统计，才是完整口径。
 *
 * 轮询放在全局而非访客会话页内，坐席停在任何页面都能收到提醒。
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { chatRecordsApi } from '@/api';
import { useUserStore } from '@/stores/user';
import { playNewMessageSound } from '@/utils/notificationSound';
import type { AccountUnreadItem, AccountUnreadResult } from '@/types/chatRecords';

/** 轮询间隔 */
const POLL_MS = 5000;

/** 单个账号的三档未读数 */
type AccountUnread = Pick<
  AccountUnreadItem,
  'unread_count' | 'mine_unread_count' | 'other_unread_count'
>;

const EMPTY_ACCOUNT_UNREAD: AccountUnread = {
  unread_count: 0,
  mine_unread_count: 0,
  other_unread_count: 0,
};

export const useAccountUnreadStore = defineStore('accountUnread', () => {
  /** 各账号未读数，key 为 account_id */
  const unreadMap = ref<Record<number, AccountUnread>>({});
  /** 可见账号范围内的未读总数 */
  const totalUnread = ref(0);
  /** 其中分配给当前坐席的部分 */
  const totalMineUnread = ref(0);
  /** 其中未分配或属于其他坐席的部分 */
  const totalOtherUnread = ref(0);

  /**
   * 新消息提醒序号：每触发一次提醒自增。
   * 用序号而不是布尔或内容，是因为相邻两次提醒的内容可能一样，组件光看内容判断不出「又来了一条」。
   */
  const newMessageTick = ref(0);

  /** 上一轮的未读数；null 表示还没有基线，首轮不触发提示音 */
  let lastMine: number | null = null;
  let lastOther: number | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let polling = false;

  /** 开关取值：后端字段 NOT NULL DEFAULT 1，取不到时按开启 */
  const isOn = (key: 'personal_msg_sound' | 'public_msg_sound') => {
    const me = useUserStore().user;
    if (!me) return false;
    return Number(me[key] ?? 1) === 1;
  };

  /**
   * 判断这一轮要不要响：两个口径各自比对增量，配对应开关。
   * 「我的」走 personal_msg_sound，「实时对话」走 public_msg_sound，两个开关独立生效。
   */
  const shouldPlay = (mine: number, other: number) => {
    const mineUp = lastMine !== null && mine > lastMine;
    const otherUp = lastOther !== null && other > lastOther;
    return (mineUp && isOn('personal_msg_sound')) || (otherUp && isOn('public_msg_sound'));
  };

  /**
   * 拉一次未读数。
   *
   * 轮询高频，失败必须静默：否则接口一抖动，响应拦截器会按这个频率反复弹错误 toast 刷屏。
   * 单次失败保留上一轮数据，下一轮自然补上。
   */
  const fetchOnce = async () => {
    try {
      const res = (await chatRecordsApi.accountsUnread()) as AccountUnreadResult | undefined;
      const map: Record<number, AccountUnread> = {};
      (res?.list ?? []).forEach((item) => {
        map[Number(item.account_id)] = {
          unread_count: Number(item.unread_count ?? 0),
          mine_unread_count: Number(item.mine_unread_count ?? 0),
          other_unread_count: Number(item.other_unread_count ?? 0),
        };
      });
      unreadMap.value = map;

      const mine = Number(res?.total_mine_unread_count ?? 0);
      const other = Number(res?.total_other_unread_count ?? 0);
      /** 首轮只记基线不响，避免刚进来就被历史未读触发 */
      if (shouldPlay(mine, other)) {
        playNewMessageSound();
        // 弹框与声音同源同开关，避免出现响了声却没弹框的错位
        newMessageTick.value += 1;
      }

      totalUnread.value = Number(res?.total_unread_count ?? 0);
      totalMineUnread.value = mine;
      totalOtherUnread.value = other;
      lastMine = mine;
      lastOther = other;
    } catch {
      /* 保留上一轮数据，等下一轮重试 */
    }
  };

  /**
   * 用「上一轮结束后再计时」而不是 setInterval：
   * 接口稍慢就会与下一次重叠，setInterval 会让请求越堆越多。
   */
  const schedule = () => {
    timer = setTimeout(async () => {
      await fetchOnce();
      if (polling) schedule();
    }, POLL_MS);
  };

  const start = () => {
    if (polling) return;
    polling = true;
    fetchOnce().then(() => {
      if (polling) schedule();
    });
  };

  const stop = () => {
    polling = false;
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  /** 退出登录时清空，避免下一个账号读到上一个人的未读数与基线 */
  const reset = () => {
    stop();
    unreadMap.value = {};
    totalUnread.value = 0;
    totalMineUnread.value = 0;
    totalOtherUnread.value = 0;
    lastMine = null;
    lastOther = null;
  };

  /** 取某账号的三档未读数，没有记录时返回全 0 */
  const unreadOf = (accountId: number): AccountUnread =>
    unreadMap.value[accountId] ?? EMPTY_ACCOUNT_UNREAD;

  return {
    unreadMap,
    newMessageTick,
    totalUnread,
    totalMineUnread,
    totalOtherUnread,
    unreadOf,
    start,
    stop,
    reset,
  };
});
